import { Component, Inject, OnInit } from '@angular/core'
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms'
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { CrudOps } from '../../../../api/constants/crud.enum'
import { Employee } from '../../../../api/models/employee.model'
import { EmployeeService } from '../../../../api/services/employee.service'
import { LoaderService } from '../../../common/services/loader.service'
import { SnackbarService } from '../../../common/services/snackbar.service'

export interface EmployeeDialogData {
    crud: CrudOps
    employee: Employee
    employees: Array<Employee>
}

@Component({
    selector: 'ppw-employee-dialog',
    templateUrl: './employee-dialog.component.html',
    styleUrls: ['./employee-dialog.component.scss']
})
export class EmployeeDialogComponent implements OnInit {
    dialogTitle: string
    employee: Employee
    crud: CrudOps
    isCreate: boolean
    isDelete: boolean
    employees!: Array<Employee>
    employeeFormGroup!: UntypedFormGroup

    private readonly prefix = 'EmployeeDialogComponent'

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private readonly loaderService: LoaderService,
        private readonly snackbarService: SnackbarService,
        private readonly employeeService: EmployeeService,
        private readonly dialogRef: MatDialogRef<EmployeeDialogComponent>,
        @Inject(MAT_DIALOG_DATA) data: EmployeeDialogData
    ) {
        this.employees = data.employees
        this.employee = data.employee
        this.crud = data.crud
        this.isCreate = this.isCrudCreate()
        this.isDelete = this.isCrudDelete()
        this.dialogTitle = this.getDialogTitle()
    }

    ngOnInit(): void {
        this.employeeFormGroup = this.createEmployeeForm()
    }

    createEmployeeForm(): UntypedFormGroup {
        return this.formBuilder.group({
            atlassianAccountId: [
                { value: this.employee ? this.employee.atlassianAccountId : '', disabled: this.isDelete },
                [this.validateAtlassianAccountId.bind(this)]
            ],
            firstName: [
                { value: this.employee ? this.employee.firstName : '', disabled: this.isDelete },
                [Validators.required]
            ],
            lastName: [
                { value: this.employee ? this.employee.lastName : '', disabled: this.isDelete },
                [Validators.required]
            ]
        })
    }

    validateAtlassianAccountId(control: AbstractControl): ValidationErrors | null {
        if (control.value === '' || control.value === undefined) {
            return null
        }

        if (!this.employee) {
            const atlassianAccountId = control.value
            const existingEmployee = this.employees.find((e) => e.atlassianAccountId === atlassianAccountId)

            return existingEmployee ? { duplicateAtlassianAccountId: true } : null
        }

        if (control.value === this.employee.atlassianAccountId) {
            return null
        }

        const atlassianAccountId = control.value
        const existingEmployee = this.employees.find((e) => e.atlassianAccountId === atlassianAccountId)

        return existingEmployee ? { duplicateAtlassianAccountId: true } : null
    }

    async action(): Promise<void> {
        if (this.isDelete) {
            await this.archiveEmployee()
        } else {
            await this.saveEmployee()
        }
    }

    async saveEmployee(): Promise<void> {
        if (this.employeeFormGroup.valid && !this.employeeFormGroup.pristine) {
            this.loaderService.startLoading()
            const employeeToSave: Employee = Employee.fromJSON({ ...this.employee, ...this.employeeFormGroup.value })
            try {
                const savedEmployee = await this.employeeService.saveEmployee(employeeToSave)
                this.employeeService.markCurrentListDirty()
                this.dialogRef.close(savedEmployee)
            } catch (err) {
                this.snackbarService.handleError(this.prefix, err, true)
                this.dialogRef.close()
            } finally {
                this.loaderService.stopLoading()
            }
        }
    }

    async archiveEmployee(): Promise<void> {
        if (this.employeeFormGroup.pristine) {
            this.loaderService.startLoading()
            try {
                await this.employeeService.archiveEmployee(this.employee)
                this.dialogRef.close(this.employee)
            } catch (err) {
                this.snackbarService.handleError(this.prefix, err, true)
                this.dialogRef.close()
            } finally {
                this.loaderService.stopLoading()
            }
        }
    }

    private getDialogTitle(): string {
        switch (this.crud) {
            case CrudOps.Create:
                return 'New Employee'
                break
            case CrudOps.Update:
                return 'Edit Employee'
                break
            case CrudOps.Delete:
                return 'Delete (= Archive) Employee'
                break
            default:
                return ''
        }
    }

    private isCrudCreate(): boolean {
        return this.crud === CrudOps.Create
    }

    private isCrudDelete(): boolean {
        return this.crud === CrudOps.Delete
    }
}
